import { InterfaceStore, createInterfaceSlice } from './slices/interface-store';
import { ChatStore, createChatSlice } from './slices/chat-store';
import { ConfigStore, createConfigSlice } from './slices/config-store';
import { createStore, useStore } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { useContext } from 'react';
import { WidgetContext } from './store-context';
import { Config } from '@/types';
import { ContextStore, createContextSlice } from './slices/context-store';
import { LocationStore, createLocationSlice } from './slices/location-store';
import { createStorageWithTTL } from '@/utils/storage-with-ttl';

/**
 * Generate storage key based on instance ID and current URL path.
 * This ensures each widget instance on each page has isolated localStorage,
 * preventing collisions between multiple instances on the same page.
 */
const getStorageKey = (instanceId: string): string => {
  const path = window.location.pathname;
  // Sanitize path: replace special chars with hyphens
  const sanitizedPath = path.replace(/[^a-zA-Z0-9]/g, '-');
  return `peptalk-${instanceId}${sanitizedPath}`;
};

export type WidgetStoreState = InterfaceStore &
  ChatStore &
  ConfigStore &
  ContextStore &
  LocationStore & {
    instanceId: string;
  };

export interface WidgetStoreProps {
  config: Config.Widget;
  instanceId: string;
}

export const createWidgetStore = (initProps: WidgetStoreProps) => {
  // Check if this is a proposal/client-facing page (has proposalId in config)
  const isProposalMode = !!initProps.config?.customContext?.proposalId;
  // Check if widget is rendered as an inline form (embedded in a target element).
  // Inline forms behave like static HTML forms: no session data should be restored
  // on page refresh, so users always start with a clean form (fixes PIF-122).
  const isInlineMode = !!initProps.config?.ui?.target;

  const store = createStore<WidgetStoreState>()(
    persist(
      (...a) => ({
        ...createConfigSlice(...a),
        ...createInterfaceSlice(...a),
        ...createChatSlice(...a),
        ...createContextSlice(...a),
        ...createLocationSlice(...a),
        ...initProps,
      }),
      {
        name: getStorageKey(initProps.instanceId), // Instance + URL-based storage key for isolation
        storage: createJSONStorage(() => createStorageWithTTL()),
        partialize: state => {
          // Don't persist anything for proposal/client-facing pages
          // Proposal sessions are ephemeral and tied to the page visit
          if (isProposalMode) {
            return {};
          }

          // Don't persist anything for inline forms (PIF-122).
          // Inline forms behave like static HTML forms: every page refresh
          // must show a blank, editable form with no saved session or form data.
          // The floating widget is NOT affected by this guard.
          if (isInlineMode) {
            return {};
          }

          // Only persist session and chat state for floating widget mode
          return {
            chat: {
              sessionId: state.chat.sessionId,
              sessionToken: state.chat.sessionToken,
              conversationId: state.chat.conversationId,
              messages: state.chat.messages,
              filledQuestionnaireData: state.chat.filledQuestionnaireData,
              lastWelcomeBackShown: state.chat.lastWelcomeBackShown,
              welcomeBackMessageIndex: state.chat.welcomeBackMessageIndex,
              lockedWelcomeMessage: state.chat.lockedWelcomeMessage,
              lockedCustomContext: state.chat.lockedCustomContext,
            },
          };
        },
        merge: (persistedState, currentState) => {
          // Enable/disable store logging (set to false for production)
          const ENABLE_STORE_LOGGING = false;

          const persistedChat = persistedState
            ? (persistedState as Partial<WidgetStoreState>).chat
            : null;

          if (ENABLE_STORE_LOGGING) {
            console.log('💾 [Widget Store] Merging persisted state:', {
              timestamp: new Date().toISOString(),
              hasPersistedData: !!persistedState,
              persistedData: {
                hasSessionId: !!persistedChat?.sessionId,
                hasSessionToken: !!persistedChat?.sessionToken,
                sessionTokenPreview:
                  persistedChat?.sessionToken?.substring(0, 30) + '...',
                sessionTokenLength: persistedChat?.sessionToken?.length,
                sessionId: persistedChat?.sessionId,
                messageCount: persistedChat?.messages?.length || 0,
              },
              currentData: {
                hasSessionId: !!currentState.chat.sessionId,
                hasSessionToken: !!currentState.chat.sessionToken,
                sessionId: currentState.chat.sessionId,
              },
            });
          }

          const persistedChatSafe = persistedChat ? { ...persistedChat } : null;

          const mergedState = {
            ...currentState,
            chat: {
              ...currentState.chat, // Keep all functions from initial state
              ...(persistedChatSafe ?? {}), // Merge persisted data
            },
          };

          if (ENABLE_STORE_LOGGING) {
            console.log('✅ [Widget Store] Merge complete:', {
              hasSessionId: !!mergedState.chat.sessionId,
              hasSessionToken: !!mergedState.chat.sessionToken,
              sessionTokenPreview:
                mergedState.chat.sessionToken?.substring(0, 30) + '...',
              sessionId: mergedState.chat.sessionId,
            });
          }

          return mergedState;
        },
      }
    )
  );

  // Set customContext from config if provided (after store initialization)
  if (initProps.config?.customContext) {
    store.getState().context.setCustomContext(initProps.config.customContext);
  }

  return store;
};

export const useWidgetStore = <T>(
  selector: (state: WidgetStoreState) => T
): T => {
  const store = useContext(WidgetContext);
  if (!store) throw new Error('Missing WidgetContext.Provider in the tree');
  return useStore(store, selector);
};
